import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentIcon,
  ChartBarIcon,
  LinkIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import {
  Alert,
  Badge,
  Button,
  Input,
  MetricCard,
  Page,
  PageBody,
  PageHeader,
  Panel,
  PanelBody,
  PanelHeader,
  PanelTitle,
  Spinner,
  Textarea,
} from '../../components/ui';
import {
  useCreateExportV1ClientOpportunityRankerWorkspacesWorkspaceIdExportsPost,
  useCreateMagicLinkV1ClientOpportunityRankerWorkspacesWorkspaceIdAccessLinksPost,
  useGetWorkspaceV1ClientOpportunityRankerWorkspacesWorkspaceIdGet,
  useImportFileRevisionV1ClientOpportunityRankerWorkspacesWorkspaceIdRevisionsImportFilePost,
  useImportFileWorkspaceV1ClientOpportunityRankerWorkspacesImportFilePost,
  useImportHtmlWorkspaceV1ClientOpportunityRankerWorkspacesImportHtmlPost,
  useImportStructuredWorkspaceV1ClientOpportunityRankerWorkspacesImportStructuredPost,
  useListAccountsV1ClientOpportunityRankerAccountsGet,
  useListMagicLinksV1ClientOpportunityRankerWorkspacesWorkspaceIdAccessLinksGet,
  useListWorkspacesV1ClientOpportunityRankerWorkspacesGet,
  useRevokeMagicLinkV1ClientOpportunityRankerAccessLinksLinkIdRevokePost,
  useSaveRevisionV1ClientOpportunityRankerWorkspacesWorkspaceIdRevisionsPost,
} from '../../generated/client-opportunity-ranker/client-opportunity-ranker';
import type { AccountListResponse, ArtifactResponse, MagicLinkListResponse, MagicLinkResponse } from '../../generated/models';
import { AXIOS_INSTANCE } from '../../services/api-client';
import {
  ComputedResults,
  OpportunityResultRow,
  RevisionDetail,
  ScoringState,
  WorkspaceDetail,
  WorkspaceListResponse,
} from './api';
import { ImportSourceFormat, downloadImportTemplate } from './importTemplates';
import { clearSourceOverrides, computeRows } from './publicScoring';

const scoreInputClass = 'w-20';

type CreatedShareLink = {
  target: string;
  url: string | null;
  link: MagicLinkResponse;
};

type TemplateFormat = 'xlsx' | 'csv' | 'json';
type ShareAccessMode = 'domain' | 'email';

function fullShareUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const origin = window.location.origin.replace(/\/$/, '');
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

function clickDownloadLink(href: string, filename?: string | null) {
  const link = document.createElement('a');
  link.href = href;
  if (filename) link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function isAuthenticatedExportDownload(url: string): boolean {
  if (/^https?:\/\//i.test(url)) return false;
  try {
    const parsed = new URL(url, window.location.origin);
    return /^\/v1\/client-opportunity-ranker\/workspaces\/\d+\/exports\/\d+\/download$/.test(parsed.pathname);
  } catch (_) {
    return false;
  }
}

async function downloadExportArtifact(artifact: ArtifactResponse): Promise<boolean> {
  if (!artifact.download_url) return false;

  if (isAuthenticatedExportDownload(artifact.download_url)) {
    const response = await AXIOS_INSTANCE.get<Blob>(artifact.download_url, { responseType: 'blob' });
    const objectUrl = window.URL.createObjectURL(response.data);
    clickDownloadLink(objectUrl, artifact.filename);
    window.setTimeout(() => {
      window.URL.revokeObjectURL(objectUrl);
    }, 0);
    return true;
  }

  clickDownloadLink(artifact.download_url, artifact.filename);
  return true;
}

function parseShareTargets(value: string, mode: ShareAccessMode): string[] {
  const seen = new Set<string>();
  return value
    .split(',')
    .map((target) => (mode === 'domain' ? normalizeShareDomain(target) : target.trim().toLowerCase()))
    .filter(Boolean)
    .filter((target) => {
      if (seen.has(target)) return false;
      seen.add(target);
      return true;
    });
}

function normalizeShareDomain(value: string): string {
  const trimmed = value.trim().toLowerCase();
  const domain = (trimmed.includes('@') ? trimmed.split('@').pop() ?? '' : trimmed).replace(/^@+/, '').replace(/\.+$/, '');
  return domain;
}

function scoreAverage(values: number[] | undefined): number {
  if (!values?.length) return 3;
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function scoreArrayFromAverage(value: string, length: number): number[] {
  const parsed = Number(value);
  const bounded = Math.max(1, Math.min(5, Number.isFinite(parsed) ? Math.round(parsed) : 3));
  return Array.from({ length: Math.max(1, length) }, () => bounded);
}

function resultRows(results: ComputedResults | undefined): OpportunityResultRow[] {
  const rows = results?.ranked_opportunities;
  return Array.isArray(rows) ? rows : [];
}

function errorDetail(error: unknown): string | null {
  const response = (error as { response?: { status?: number; data?: unknown } })?.response;
  const data = response?.data as { detail?: unknown } | undefined;
  if (typeof data?.detail === 'string') return data.detail;
  if (response?.status === 404) {
    return 'The Opportunity Ranker API route was not found. Confirm the local backend is running this branch.';
  }
  if (response?.status === 403) return 'You need Opportunity Ranker admin permission to import files.';
  if (response?.status === 413) return 'This file is too large for Opportunity Ranker import.';
  return null;
}

function formatPriority(row: OpportunityResultRow | undefined): string {
  if (!row) return '—';
  const value = row.priority_score ?? row.composite_score;
  if (value === undefined || value === null) return '—';
  const formatted = Number.isInteger(value) ? String(value) : value.toFixed(2);
  return `${formatted}${row.priority_score_source === 'source_indexes' ? '*' : ''}`;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function linkStatus(link: MagicLinkResponse): { label: string; variant: 'success' | 'warning' | 'danger' | 'secondary' } {
  const expiresAt = new Date(link.expires_at).getTime();
  if (link.revoked_at) return { label: 'Revoked', variant: 'danger' };
  if (!link.is_active || Number.isNaN(expiresAt) || expiresAt <= Date.now()) return { label: 'Expired', variant: 'warning' };
  if (link.redeemed_count >= link.max_redemptions) return { label: 'Redeemed', variant: 'secondary' };
  return { label: 'Active', variant: 'success' };
}

function emailDeliveryMessage(link: MagicLinkResponse | null): string | null {
  if (!link?.email_delivery_status) return null;
  if (link.email_delivery_status === 'email_queued') return 'Email queued. The full link is also shown below for copying.';
  if (link.email_delivery_status === 'email_not_configured') {
    return 'Email is not configured here yet. Copy this URL now or create a fresh link later.';
  }
  if (link.email_delivery_status === 'email_not_applicable') {
    return 'Domain links are not emailed automatically. Copy this URL and the access code now.';
  }
  if (link.email_delivery_status === 'email_failed') {
    return `Email failed to queue${link.email_delivery_error ? `: ${link.email_delivery_error}` : '.'}`;
  }
  return link.email_delivery_status;
}

export default function ClientOpportunityRankerPage() {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canWriteAction = hasPermission('client_opportunity_ranker:write');
  const canExportAction = hasPermission('client_opportunity_ranker:export');
  const canShareAction = hasPermission('client_opportunity_ranker:share');
  const canAdminAction = hasPermission('client_opportunity_ranker:admin');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(null);
  const [draftState, setDraftState] = useState<ScoringState | null>(null);
  const [scoreEditedIds, setScoreEditedIds] = useState<Set<string>>(() => new Set());
  const [importAccountName, setImportAccountName] = useState('');
  const [parentAccountChoice, setParentAccountChoice] = useState('none');
  const [parentAccountName, setParentAccountName] = useState('');
  const [importWorkspaceTitle, setImportWorkspaceTitle] = useState('');
  const [importContent, setImportContent] = useState('');
  const [importSourceFormat, setImportSourceFormat] = useState<ImportSourceFormat>('csv');
  const [workspaceImportFile, setWorkspaceImportFile] = useState<File | null>(null);
  const [revisionImportFile, setRevisionImportFile] = useState<File | null>(null);
  const [revisionImportNotes, setRevisionImportNotes] = useState('');
  const [templateFormat, setTemplateFormat] = useState<TemplateFormat>('xlsx');
  const [lastImportSummary, setLastImportSummary] = useState<Record<string, unknown> | null>(null);
  const [shareAccessMode, setShareAccessMode] = useState<ShareAccessMode>('domain');
  const [shareEmail, setShareEmail] = useState('');
  const [shareExpiryHours, setShareExpiryHours] = useState(168);
  const [shareSendEmail, setShareSendEmail] = useState(false);
  const [lastCreatedLink, setLastCreatedLink] = useState<MagicLinkResponse | null>(null);
  const [createdShareLinks, setCreatedShareLinks] = useState<CreatedShareLink[]>([]);
  const [shareIsCreating, setShareIsCreating] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [exportDownloadError, setExportDownloadError] = useState<string | null>(null);

  const workspacesQuery = useListWorkspacesV1ClientOpportunityRankerWorkspacesGet<WorkspaceListResponse>({
    query: {
      queryKey: ['client-opportunity-ranker', 'workspaces'],
    },
  });

  const accountsQuery = useListAccountsV1ClientOpportunityRankerAccountsGet<AccountListResponse>({
    query: {
      queryKey: ['client-opportunity-ranker', 'accounts'],
    },
  });

  useEffect(() => {
    if (selectedWorkspaceId || !workspacesQuery.data?.items.length) return;
    setSelectedWorkspaceId(workspacesQuery.data.items[0].id);
  }, [selectedWorkspaceId, workspacesQuery.data?.items]);

  const workspaceQuery = useGetWorkspaceV1ClientOpportunityRankerWorkspacesWorkspaceIdGet<WorkspaceDetail>(
    selectedWorkspaceId ?? 0,
    {
      query: {
        queryKey: ['client-opportunity-ranker', 'workspace', selectedWorkspaceId],
        enabled: selectedWorkspaceId !== null,
      },
    }
  );

  const accessLinksQuery = useListMagicLinksV1ClientOpportunityRankerWorkspacesWorkspaceIdAccessLinksGet<MagicLinkListResponse>(
    selectedWorkspaceId ?? 0,
    {
      query: {
        queryKey: ['client-opportunity-ranker', 'access-links', selectedWorkspaceId],
        enabled: Boolean(canShareAction && selectedWorkspaceId !== null),
      },
    }
  );

  const latestRevision = (workspaceQuery.data?.latest_revision ?? null) as RevisionDetail | null;

  useEffect(() => {
    if (latestRevision?.scoring_state) {
      setDraftState(latestRevision.scoring_state);
      setScoreEditedIds(new Set());
    }
  }, [latestRevision?.id, latestRevision?.scoring_state]);

  const importMutation = useImportHtmlWorkspaceV1ClientOpportunityRankerWorkspacesImportHtmlPost({
    mutation: {
      onSuccess: (data) => {
        setSelectedWorkspaceId(data.workspace.id);
        setDraftState((data.revision as RevisionDetail).scoring_state);
        setScoreEditedIds(new Set());
        setImportContent('');
        setLastImportSummary(data.import_summary as Record<string, unknown>);
        queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker'] });
      },
    },
  });

  const structuredImportMutation = useImportStructuredWorkspaceV1ClientOpportunityRankerWorkspacesImportStructuredPost({
    mutation: {
      onSuccess: (data) => {
        setSelectedWorkspaceId(data.workspace.id);
        setDraftState((data.revision as RevisionDetail).scoring_state);
        setScoreEditedIds(new Set());
        setImportContent('');
        setLastImportSummary(data.import_summary as Record<string, unknown>);
        queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker'] });
      },
    },
  });

  const fileImportMutation = useImportFileWorkspaceV1ClientOpportunityRankerWorkspacesImportFilePost({
    mutation: {
      onSuccess: (data) => {
        setSelectedWorkspaceId(data.workspace.id);
        setDraftState((data.revision as RevisionDetail).scoring_state);
        setScoreEditedIds(new Set());
        setWorkspaceImportFile(null);
        setLastImportSummary(data.import_summary as Record<string, unknown>);
        queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker'] });
      },
    },
  });

  const revisionFileImportMutation =
    useImportFileRevisionV1ClientOpportunityRankerWorkspacesWorkspaceIdRevisionsImportFilePost({
      mutation: {
        onSuccess: (data) => {
          setDraftState((data.revision as RevisionDetail).scoring_state);
          setScoreEditedIds(new Set());
          setRevisionImportFile(null);
          setRevisionImportNotes('');
          setLastImportSummary(data.import_summary as Record<string, unknown>);
          queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker'] });
        },
      },
    });

  const saveMutation = useSaveRevisionV1ClientOpportunityRankerWorkspacesWorkspaceIdRevisionsPost({
    mutation: {
      onSuccess: (revision) => {
        setDraftState((revision as RevisionDetail).scoring_state);
        setScoreEditedIds(new Set());
        queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker'] });
      },
    },
  });

  const exportMutation = useCreateExportV1ClientOpportunityRankerWorkspacesWorkspaceIdExportsPost({
    mutation: {
      onSuccess: async (artifact) => {
        try {
          if (await downloadExportArtifact(artifact)) {
            setExportDownloadError(null);
            setExportMessage(`${artifact.filename || 'Export'} is ready to download.`);
            return;
          }
          setExportMessage(null);
          setExportDownloadError('Export was created, but no download URL was returned. Try again or check storage configuration.');
        } catch (_) {
          setExportMessage(null);
          setExportDownloadError('Export was created, but the download could not be started. Try again or check backend logs.');
        }
      },
    },
  });

  const shareMutation = useCreateMagicLinkV1ClientOpportunityRankerWorkspacesWorkspaceIdAccessLinksPost();

  const revokeLinkMutation = useRevokeMagicLinkV1ClientOpportunityRankerAccessLinksLinkIdRevokePost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker', 'access-links', selectedWorkspaceId] });
      },
    },
  });

  const importIsPending =
    importMutation.isPending ||
    structuredImportMutation.isPending ||
    fileImportMutation.isPending ||
    revisionFileImportMutation.isPending;
  const importHasError =
    importMutation.isError ||
    structuredImportMutation.isError ||
    fileImportMutation.isError ||
    revisionFileImportMutation.isError;
  const importErrorMessage =
    errorDetail(fileImportMutation.error) ||
    errorDetail(revisionFileImportMutation.error) ||
    errorDetail(importMutation.error) ||
    errorDetail(structuredImportMutation.error) ||
    'Unable to import this opportunity ranking file.';
  const importCanSubmit = Boolean(
    canAdminAction &&
    importAccountName &&
    importContent.length >= (importSourceFormat === 'html' ? 100 : 10) &&
    !importIsPending
  );
  const importButtonLabel = importSourceFormat === 'html' ? 'Import HTML Workspace' : 'Import Template Workspace';
  const importPlaceholder =
    importSourceFormat === 'html'
      ? 'Paste the exported CARET/Miro ranking HTML'
      : `Paste a filled opportunity-ranker-import-template.${importSourceFormat}`;
  const importHelpText =
    importSourceFormat === 'html'
      ? 'Use this only for the original CARET/Miro artifact shape.'
      : 'Use this for portfolio-company readouts. Fill factor scores, evidence, wave, confidence, owners, and data-readiness fields before importing.';

  useEffect(() => {
    setImportContent('');
    importMutation.reset();
    structuredImportMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importSourceFormat]);

  const importSummaryItems = useMemo<Array<[string, unknown]>>(() => {
    if (!lastImportSummary) return [];
    const items: Array<[string, unknown]> = [
      ['Opportunities', lastImportSummary.opportunity_count],
      ['Themes', lastImportSummary.theme_count],
      ['Tagged rows', lastImportSummary.strategic_tagged_count],
      ['Source counts', lastImportSummary.source_counts],
      ['Status counts', lastImportSummary.status_counts],
    ];
    return items.filter(([, value]) => value !== undefined && value !== null);
  }, [lastImportSummary]);

  const parentAccountOptions = useMemo(
    () =>
      (accountsQuery.data?.items ?? []).filter(
        (account) => account.account_type === 'holding_company' || account.parent_account_id == null
      ),
    [accountsQuery.data?.items]
  );

  const savedRows = resultRows(latestRevision?.computed_results);
  const accessLinks = canShareAction ? accessLinksQuery.data?.items ?? [] : [];
  const shareTargets = useMemo(() => parseShareTargets(shareEmail, shareAccessMode), [shareAccessMode, shareEmail]);
  const shareCanSubmit = Boolean(canShareAction && selectedWorkspaceId && shareTargets.length > 0 && !shareIsCreating);
  const draftRows = useMemo(() => draftState?.opportunities ?? [], [draftState]);
  const draftResultRows = useMemo(
    () => (draftState ? computeRows(draftState, latestRevision?.computed_results, { recomputeIds: scoreEditedIds }) : savedRows),
    [draftState, latestRevision?.computed_results, savedRows, scoreEditedIds]
  );
  const draftRowById = useMemo(
    () => new Map(draftRows.map((row) => [row.id, row])),
    [draftRows]
  );

  const updateOpportunityScore = (id: string, key: 'gain' | 'pain', value: string) => {
    if (!canWriteAction) return;
    setScoreEditedIds((current) => new Set(current).add(id));
    setDraftState((current) => {
      if (!current) return current;
      return {
        ...current,
        opportunities: current.opportunities.map((row) =>
          row.id === id
            ? clearSourceOverrides({ ...row, [key]: scoreArrayFromAverage(value, row[key]?.length ?? 5) })
            : row
        ),
      };
    });
  };

  const handleImport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canAdminAction) return;
    if (importSourceFormat === 'html') {
      importMutation.mutate({
        data: {
          account_name: importAccountName,
          workspace_title: importWorkspaceTitle,
          html_content: importContent,
        },
      });
      return;
    }
    structuredImportMutation.mutate({
      data: {
        account_name: importAccountName,
        workspace_title: importWorkspaceTitle,
        source_format: importSourceFormat,
        content: importContent,
      },
    });
  };

  const handleWorkspaceFileImport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canAdminAction || !workspaceImportFile || !importAccountName) return;
    const selectedParentId =
      parentAccountChoice !== 'none' && parentAccountChoice !== 'new' ? Number(parentAccountChoice) : undefined;
    const newParentName = parentAccountChoice === 'new' ? parentAccountName.trim() : undefined;
    fileImportMutation.mutate({
      data: {
        file: workspaceImportFile,
        account_name: importAccountName,
        parent_account_id: selectedParentId,
        parent_account_name: newParentName || undefined,
        workspace_title: importWorkspaceTitle || undefined,
      },
    });
  };

  const handleRevisionFileImport = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canWriteAction || !revisionImportFile || !selectedWorkspaceId || !latestRevision) return;
    revisionFileImportMutation.mutate({
      workspaceId: selectedWorkspaceId,
      data: {
        file: revisionImportFile,
        base_revision_number: latestRevision.revision_number,
        notes: revisionImportNotes || undefined,
      },
    });
  };

  const canSave = Boolean(canWriteAction && selectedWorkspaceId && draftState && latestRevision && !saveMutation.isPending);
  const canExport = Boolean(canExportAction && selectedWorkspaceId && latestRevision && !exportMutation.isPending);
  const canImportWorkspaceFile = Boolean(
    canAdminAction &&
      importAccountName &&
      workspaceImportFile &&
      !importIsPending &&
      (parentAccountChoice !== 'new' || parentAccountName.trim())
  );
  const canImportRevisionFile = Boolean(canWriteAction && selectedWorkspaceId && latestRevision && revisionImportFile && !importIsPending);

  const saveDraft = () => {
    if (!canSave || !selectedWorkspaceId || !draftState) return;
    saveMutation.mutate({
      workspaceId: selectedWorkspaceId,
      data: {
        base_revision_number: latestRevision?.revision_number ?? 0,
        scoring_state: draftState,
      },
    });
  };

  const createExport = (artifactType: 'json' | 'csv' | 'readout_csv' | 'editable_csv') => {
    if (!canExport || !selectedWorkspaceId || !latestRevision) return;
    setExportMessage(null);
    setExportDownloadError(null);
    exportMutation.mutate({
      workspaceId: selectedWorkspaceId,
      data: {
        artifact_type: artifactType as any,
        revision_id: latestRevision.id,
      },
    });
  };

  const createShareLink = async () => {
    if (!shareCanSubmit || !selectedWorkspaceId || shareTargets.length === 0) return;
    setShareIsCreating(true);
    setCreatedShareLinks([]);
    setLastCreatedLink(null);
    const results: CreatedShareLink[] = [];
    try {
      const expiresInHours = Math.max(1, Math.min(720, Number.isFinite(shareExpiryHours) ? shareExpiryHours : 168));
      for (const target of shareTargets) {
        const link = await shareMutation.mutateAsync({
          workspaceId: selectedWorkspaceId,
          data: {
            workspace_id: selectedWorkspaceId,
            access_mode: shareAccessMode,
            viewer_email: target,
            expires_in_hours: expiresInHours,
            send_email: shareAccessMode === 'email' && shareSendEmail,
          },
        });
        const created = { target, url: fullShareUrl(link.magic_url_path), link };
        results.push(created);
        setCreatedShareLinks((current) => [...current, created]);
        setLastCreatedLink(link);
      }
    } catch {
      // React Query owns the visible error state; keep any links already returned visible.
    } finally {
      if (results.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['client-opportunity-ranker', 'access-links', selectedWorkspaceId] });
      }
      setShareIsCreating(false);
    }
  };

  const copyShareText = (value: string | null | undefined) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
  };

  const createUpdateWorkspacePanel = (
    <Panel>
      <PanelHeader>
        <PanelTitle>Create or Update Workspace</PanelTitle>
      </PanelHeader>
      <PanelBody className="space-y-4">
        <form className="space-y-3 rounded-md border border-gray-200 p-3 dark:border-dark-border" onSubmit={handleWorkspaceFileImport}>
          <div>
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Create a new workspace</h3>
          </div>
          <Input
            value={importAccountName}
            onChange={(event) => setImportAccountName(event.target.value)}
            placeholder="Account name"
            disabled={!canAdminAction}
          />
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Holding company
            <select
              aria-label="Holding company"
              className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-charcoal dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100"
              value={parentAccountChoice}
              disabled={!canAdminAction}
              onChange={(event) => {
                setParentAccountChoice(event.target.value);
                if (event.target.value !== 'new') setParentAccountName('');
              }}
            >
              <option value="none">No holding company</option>
              <option value="new">Create new holding company</option>
              {parentAccountOptions.map((account) => (
                <option key={account.id} value={String(account.id)}>
                  {account.name}
                </option>
              ))}
            </select>
          </label>
          {parentAccountChoice === 'new' && (
            <Input
              value={parentAccountName}
              onChange={(event) => setParentAccountName(event.target.value)}
              placeholder="Holding company name"
              disabled={!canAdminAction}
            />
          )}
          <Input
            value={importWorkspaceTitle}
            onChange={(event) => setImportWorkspaceTitle(event.target.value)}
            placeholder="Workspace title"
            disabled={!canAdminAction}
          />
          <input
            type="file"
            accept=".xlsx,.csv,.json,.html,.htm"
            className="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm file:font-medium dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100 dark:file:bg-dark-surface-2"
            disabled={!canAdminAction}
            onChange={(event) => setWorkspaceImportFile(event.target.files?.[0] ?? null)}
          />
          <Button
            type="submit"
            disabled={!canImportWorkspaceFile}
            className="w-full"
          >
            {fileImportMutation.isPending ? <Spinner size="sm" /> : <ArrowUpTrayIcon className="h-4 w-4" />}
            Create new workspace
          </Button>
        </form>

        <form className="space-y-3 rounded-md border border-gray-200 p-3 dark:border-dark-border" onSubmit={handleRevisionFileImport}>
          <div>
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">Update selected workspace</h3>
            <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
              {workspaceQuery.data?.workspace.title ?? 'No workspace selected'}
            </p>
          </div>
          <input
            type="file"
            accept=".xlsx,.csv,.json,.html,.htm"
            className="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-charcoal file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-1.5 file:text-sm file:font-medium dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100 dark:file:bg-dark-surface-2"
            disabled={!canWriteAction}
            onChange={(event) => setRevisionImportFile(event.target.files?.[0] ?? null)}
          />
          <Input
            value={revisionImportNotes}
            onChange={(event) => setRevisionImportNotes(event.target.value)}
            placeholder="Revision notes"
            disabled={!canWriteAction}
          />
          <Button
            type="submit"
            variant="outline"
            disabled={!canImportRevisionFile}
            className="w-full"
          >
            {revisionFileImportMutation.isPending ? <Spinner size="sm" /> : <ArrowUpTrayIcon className="h-4 w-4" />}
            Upload new revision
          </Button>
        </form>

        <details className="rounded-md border border-gray-200 p-3 dark:border-dark-border">
          <summary className="cursor-pointer text-sm font-medium text-charcoal dark:text-gray-100">
            Workspace Templates
          </summary>
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Format
              <select
                aria-label="Workspace template format"
                className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-charcoal dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100"
                value={templateFormat}
                onChange={(event) => setTemplateFormat(event.target.value as TemplateFormat)}
              >
                <option value="xlsx">Excel workbook</option>
                <option value="csv">CSV file</option>
                <option value="json">JSON file</option>
              </select>
            </label>
            <Button type="button" variant="outline" onClick={() => downloadImportTemplate(templateFormat)}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download
            </Button>
          </div>
        </details>

        {importSummaryItems.length > 0 && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs dark:border-dark-border dark:bg-dark-surface-2">
            <div className="font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Last import
            </div>
            <dl className="mt-2 space-y-1">
              {importSummaryItems.map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
                  <dd className="max-w-44 truncate text-right font-medium text-charcoal dark:text-gray-100">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {importHasError && (
          <Alert variant="error">{importErrorMessage}</Alert>
        )}

        <details className="border-t border-gray-200 pt-4 dark:border-dark-border">
          <summary className="cursor-pointer text-sm font-medium text-charcoal dark:text-gray-100">
            Advanced: paste CSV, JSON, or legacy HTML
          </summary>
          <form className="mt-3 space-y-3" onSubmit={handleImport}>
            <select
              className="h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100"
              value={importSourceFormat}
              disabled={!canAdminAction}
              onChange={(event) => setImportSourceFormat(event.target.value as ImportSourceFormat)}
            >
              <option value="csv">CSV template</option>
              <option value="json">JSON template</option>
              <option value="html">CARET/Miro HTML</option>
            </select>
            <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">{importHelpText}</p>
            <Textarea
              value={importContent}
              onChange={(event) => setImportContent(event.target.value)}
              placeholder={importPlaceholder}
              rows={8}
              disabled={!canAdminAction}
            />
            <Button
              type="submit"
              disabled={!importCanSubmit}
              className="w-full"
            >
              {importMutation.isPending || structuredImportMutation.isPending ? (
                <Spinner size="sm" />
              ) : (
                <PlusIcon className="h-4 w-4" />
              )}
              {importButtonLabel}
            </Button>
          </form>
        </details>
      </PanelBody>
    </Panel>
  );

  const shareWorkspacePanel = (
    <Panel>
      <PanelHeader>
        <PanelTitle>Share selected workspace</PanelTitle>
      </PanelHeader>
      <PanelBody className="space-y-3">
        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Share mode
          <select
            aria-label="Share mode"
            className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-normal normal-case tracking-normal text-charcoal dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100"
            value={shareAccessMode}
            disabled={!canShareAction}
            onChange={(event) => {
              const nextMode = event.target.value as ShareAccessMode;
              setShareAccessMode(nextMode);
              setShareSendEmail(nextMode === 'email');
            }}
          >
            <option value="domain">Domain link</option>
            <option value="email">Restricted email link</option>
          </select>
        </label>
        <Input
          type="text"
          inputMode={shareAccessMode === 'email' ? 'email' : 'text'}
          value={shareEmail}
          onChange={(event) => setShareEmail(event.target.value)}
          placeholder={shareAccessMode === 'domain' ? 'thl.com, bazaarvoice.com' : 'client@example.com, partner@example.com'}
          aria-label={shareAccessMode === 'domain' ? 'Allowed email domains' : 'Recipient emails'}
          disabled={!canShareAction}
        />
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Expiration hours
            <Input
              type="number"
              min={1}
              max={720}
              value={shareExpiryHours}
              onChange={(event) => setShareExpiryHours(Number(event.target.value))}
              className="mt-1"
              disabled={!canShareAction}
            />
          </label>
          <label className="flex h-10 items-center gap-2 rounded-xl border border-gray-200 px-3 text-sm text-gray-600 dark:border-dark-border/50 dark:text-gray-300">
            <input
              type="checkbox"
              checked={shareSendEmail}
              onChange={(event) => setShareSendEmail(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-eliza-red focus:ring-eliza-red"
              disabled={!canShareAction || shareAccessMode === 'domain'}
            />
            Email
          </label>
        </div>
        <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
          {shareAccessMode === 'domain'
            ? 'Domain links require a matching email domain plus the creation-only access code shown after creation. Public edits save only to that viewer’s view.'
            : 'Email links require the exact recipient email and are best for one named recipient.'}
        </p>
        {shareTargets.length > 1 && (
          <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
            Creating one {shareAccessMode === 'domain' ? 'domain' : 'email-bound'} link for each of {shareTargets.length} targets.
          </p>
        )}
        <Button
          variant="outline"
          disabled={!shareCanSubmit}
          onClick={() => { void createShareLink(); }}
          className="w-full"
        >
          {shareIsCreating ? <Spinner size="sm" /> : <LinkIcon className="h-4 w-4" />}
          {shareAccessMode === 'domain' ? 'Create Access Link' : 'Create Magic Link'}{shareTargets.length > 1 ? 's' : ''}
        </Button>
        {createdShareLinks.length > 0 && (
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-dark-border dark:bg-dark-surface-2">
            <label className="text-micro font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Newly created link{createdShareLinks.length > 1 ? 's' : ''}
            </label>
            <div className="mt-2 space-y-2">
              {createdShareLinks.map((created) => (
                <div key={`${created.link.id}-${created.target}`} className="space-y-1">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-300">
                    {created.link.access_mode === 'domain' ? `*@${created.target}` : created.target}
                  </div>
                  <div className="flex gap-2">
                    <Input readOnly value={created.url ?? ''} className="font-mono text-xs" />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => copyShareText(created.url)}
                      aria-label={`Copy ${created.link.access_mode === 'domain' ? 'access' : 'magic'} link for ${created.target}`}
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  {created.link.access_mode === 'domain' && created.link.access_code && (
                    <div className="flex gap-2">
                      <Input readOnly value={created.link.access_code} className="font-mono text-xs uppercase" />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => copyShareText(created.link.access_code)}
                        aria-label={`Copy access code for ${created.target}`}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
              {emailDeliveryMessage(lastCreatedLink) ??
                'Each link is expiring, revocable, and visible only at creation time. Public edits save to each viewer’s saved view.'}
            </p>
          </div>
        )}
      </PanelBody>
    </Panel>
  );

  const linkHistoryPanel = (
    <Panel>
      <PanelHeader className="flex items-center justify-between gap-3">
        <div>
          <PanelTitle>Link history</PanelTitle>
          <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
            Full URLs are shown only when created. If a client loses a link, create a fresh one.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => accessLinksQuery.refetch()}
          disabled={!canShareAction || !selectedWorkspaceId || accessLinksQuery.isLoading}
          aria-label="Refresh access links"
        >
          <ArrowPathIcon className="h-4 w-4" />
        </Button>
      </PanelHeader>
      <PanelBody>
        {!canShareAction ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Share permission is required to view workspace access links.
          </p>
        ) : accessLinksQuery.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Spinner size="sm" />
            Loading access links
          </div>
        ) : accessLinks.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No links created for this workspace yet.
          </p>
        ) : (
          <div className="space-y-2">
            {accessLinks.map((link) => {
              const status = linkStatus(link);
              const canRevoke = canShareAction && link.is_active && !link.revoked_at && status.label !== 'Expired';
              return (
                <div
                  key={link.id}
                  className="rounded-md border border-gray-200 bg-white p-3 text-xs dark:border-dark-border dark:bg-dark-surface"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={status.variant}>{status.label}</Badge>
                        {link.access_mode === 'domain' && link.access_code_required && <Badge variant="secondary">Code required</Badge>}
                        <span className="truncate font-medium text-charcoal dark:text-gray-100">
                          {link.label ||
                            (link.access_mode === 'domain' && link.viewer_email_domain
                              ? `*@${link.viewer_email_domain}`
                              : link.viewer_email_domain
                                ? `Email link for ${link.viewer_email_domain}`
                                : 'Email-bound link')}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-gray-500 dark:text-gray-400">
                        <span>Created {formatDateTime(link.created_at)}</span>
                        <span>Expires {formatDateTime(link.expires_at)}</span>
                        <span>Redeemed {link.redeemed_count}/{link.max_redemptions}</span>
                        <span>Last used {formatDateTime(link.last_redeemed_at)}</span>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => revokeLinkMutation.mutate({ linkId: link.id })}
                      disabled={!canRevoke || revokeLinkMutation.isPending}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PanelBody>
    </Panel>
  );

  return (
    <Layout>
      <Page layout="full-width" maxWidth="full">
        <PageHeader
          title="Opportunity Ranker"
          description="Client-facing opportunity ranking workspace with saved revisions and share links."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" disabled={!canExport} onClick={() => createExport('editable_csv')}>
                <ArrowDownTrayIcon className="h-4 w-4" />
                Editable CSV
              </Button>
              <Button variant="outline" disabled={!canExport} onClick={() => createExport('readout_csv')}>
                <ArrowDownTrayIcon className="h-4 w-4" />
                Readout CSV
              </Button>
              <Button variant="outline" disabled={!canExport} onClick={() => createExport('json')}>
                <ArrowDownTrayIcon className="h-4 w-4" />
                JSON
              </Button>
              <Button onClick={saveDraft} disabled={!canSave}>
                {saveMutation.isPending ? <Spinner size="sm" /> : null}
                Save Revision
              </Button>
            </div>
          }
        />
        <PageBody className="space-y-4">
          {workspacesQuery.isError && (
            <Alert variant="error">Unable to load opportunity workspaces.</Alert>
          )}
          {saveMutation.isError && (
            <Alert variant="error">This workspace changed since the loaded revision. Refresh before saving again.</Alert>
          )}
          {exportMutation.isError && (
            <Alert variant="error">Export mirroring failed. The canonical revision is still saved in Postgres.</Alert>
          )}
          {exportDownloadError && (
            <Alert variant="error">{exportDownloadError}</Alert>
          )}
          {exportMessage && (
            <Alert variant="success">{exportMessage}</Alert>
          )}
          {createdShareLinks.length > 0 && (
            <Alert variant="success">
              {createdShareLinks.length === 1 ? 'Share link created.' : `${createdShareLinks.length} share links created.`} Copy from the newly created links section.
            </Alert>
          )}
          {shareMutation.isError && (
            <Alert variant="error">Unable to create one or more magic links. Check the allowed domain or email list and try again.</Alert>
          )}

          <div className="grid gap-4 md:grid-cols-4">
            <MetricCard
              title="Workspaces"
              value={workspacesQuery.data?.items.length ?? 0}
              icon={<ChartBarIcon className="h-5 w-5" />}
            />
            <MetricCard
              title="Opportunities"
              value={latestRevision?.computed_results.opportunity_count ?? draftRows.length}
            />
            <MetricCard title="Themes" value={latestRevision?.computed_results.theme_count ?? 0} />
            <MetricCard title="Revision" value={latestRevision?.revision_number ?? '—'} />
          </div>

          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            <Panel>
              <PanelHeader className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <PanelTitle>Ranking Workspace</PanelTitle>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {workspaceQuery.data?.workspace.title ?? 'Select or import a ranking workspace.'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm text-charcoal dark:border-dark-border/50 dark:bg-dark-surface dark:text-gray-100"
                    value={selectedWorkspaceId ?? ''}
                    onChange={(event) => setSelectedWorkspaceId(event.target.value ? Number(event.target.value) : null)}
                  >
                    <option value="">Select workspace</option>
                    {workspacesQuery.data?.items.map((workspace) => (
                      <option key={workspace.id} value={workspace.id}>{workspace.title}</option>
                    ))}
                  </select>
                  <Button variant="ghost" onClick={() => workspaceQuery.refetch()} disabled={!selectedWorkspaceId}>
                    <ArrowPathIcon className="h-4 w-4" />
                    Refresh
                  </Button>
                </div>
              </PanelHeader>
              <PanelBody className="overflow-x-auto p-0">
                {workspaceQuery.isLoading ? (
                  <div className="flex min-h-64 items-center justify-center">
                    <Spinner size="lg" />
                  </div>
                ) : draftRows.length === 0 ? (
                  <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
                    Import an Opportunity Ranker workbook or CSV to start a workspace.
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-dark-border">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-dark-surface-2 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3">Rank</th>
                        <th className="px-4 py-3">Opportunity</th>
                        <th className="px-4 py-3">Theme</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Gain factor avg</th>
                        <th className="px-4 py-3">Pain factor avg</th>
                        <th className="px-4 py-3">Priority</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                      {draftResultRows.map((displayRow) => {
                        const row = draftRowById.get(displayRow.id);
                        if (!row) return null;
                        return (
                          <tr key={row.id} className="bg-white dark:bg-dark-surface">
                            <td className="px-4 py-3 font-medium">{displayRow.rank ?? '—'}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-charcoal dark:text-white">{row.name}</div>
                              <div className="text-xs text-gray-500">{row.id} · {row.source ?? 'unknown'}</div>
                            </td>
                            <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{displayRow.theme_name ?? row.theme ?? '—'}</td>
                            <td className="px-4 py-3"><Badge variant="secondary">{row.status ?? 'active'}</Badge></td>
                            <td className="px-4 py-3">
                              <Input
                                type="number"
                                min={1}
                                max={5}
                                aria-label={`${row.name} gain factor average`}
                                value={scoreAverage(row.gain)}
                                onChange={(event) => updateOpportunityScore(row.id, 'gain', event.target.value)}
                                className={scoreInputClass}
                                disabled={!canWriteAction}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <Input
                                type="number"
                                min={1}
                                max={5}
                                aria-label={`${row.name} pain factor average`}
                                value={scoreAverage(row.pain)}
                                onChange={(event) => updateOpportunityScore(row.id, 'pain', event.target.value)}
                                className={scoreInputClass}
                                disabled={!canWriteAction}
                              />
                            </td>
                            <td className="px-4 py-3 font-semibold">{formatPriority(displayRow)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </PanelBody>
            </Panel>

            <div className="space-y-4">
              {createUpdateWorkspacePanel}
              {shareWorkspacePanel}
              {linkHistoryPanel}
            </div>
          </div>
        </PageBody>
      </Page>
    </Layout>
  );
}
